import { hourMs, minuteMs } from "verdi-common/timeConstants";
import { HistoricalDataAPI } from "verdi-web-client/APICommands/HistoricalDataAPI";
import type { HistoricalDataResponse } from "verdi-web-client/APICommands/HistoricalDataTypes";
import type { LegacyZoneHistorySignal } from "../ChartSpec";
import type { EventDetector, Sample } from "./detect-events";

const SCHEDULED_DATA_TYPE = "zoneIrrigationScheduled";
const VALVE_OPEN_DATA_TYPE = "valveOpenPercent";

export type LegacyZoneHistory = Record<LegacyZoneHistorySignal, Sample[]>;

export const LEGACY_ZONE_HISTORY_DETECTORS: Record<LegacyZoneHistorySignal, EventDetector> = {
    // Same bands as the new scheduler's schedule history, not legacy's > 0.25.
    scheduled: { rule: { kind: "band", onAbove: 0.7, offBelow: 0.3 }, staleAfterMs: 2 * hourMs },
    "valve-open": { rule: { kind: "threshold", onAbove: 0.5 }, staleAfterMs: 30 * minuteMs },
};

const byTime = (a: Sample, b: Sample) => a.t - b.t;

export function toSamples(response: HistoricalDataResponse): Sample[] {
    return (response.data ?? [])
        .filter((point) => Number.isFinite(point.value))
        .map((point) => ({ t: point.date.getTime(), value: point.value }));
}

export async function fetchLegacyZoneHistory(
    zoneId: string,
    startMs: number,
    endMs: number,
): Promise<LegacyZoneHistory> {
    const responses = await HistoricalDataAPI.getData(
        new Date(startMs),
        new Date(endMs),
        [zoneId],
        [SCHEDULED_DATA_TYPE, VALVE_OPEN_DATA_TYPE],
    );
    // The declared dataType union does not list these two types.
    const samplesOf = (dataType: string) =>
        responses
            .filter((response) => (response.dataType as string) === dataType)
            .flatMap(toSamples)
            .sort(byTime);
    return { scheduled: samplesOf(SCHEDULED_DATA_TYPE), "valve-open": samplesOf(VALVE_OPEN_DATA_TYPE) };
}
